import React, { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Calendar as CalendarIcon, ChevronDown } from 'lucide-react';
import {
  PRESET_LABELS,
  DEFAULT_PRESETS,
  describeRange,
} from '../../utils/dateRangeHelpers';

/**
 * Unified, fully-controlled date range filter. Pages keep owning their state
 * (dateRange, customStartDate, customEndDate) — this component only renders.
 *
 * Props:
 *   value         — current preset key (string), e.g. 'last30' or 'custom'
 *   customStart   — Date | null, used only when value === 'custom'
 *   customEnd     — Date | null
 *   onChange      — (range, start, end) => void
 *                   • Preset selected:  onChange('last30', null, null)
 *                   • Custom started:   onChange('custom', null, null) + picker opens
 *                   • Custom finished:  onChange('custom', startDate, endDate)
 *   presets       — array of preset keys to list (default = DEFAULT_PRESETS)
 *   variant       — 'dropdown' (default) | 'tabs'
 *   align         — 'right' (default) | 'left' — where dropdown anchors
 *   className     — optional outer className
 *
 * Usage:
 *   <DateRangeFilter
 *     value={dateRange}
 *     customStart={customStartDate}
 *     customEnd={customEndDate}
 *     onChange={(r, s, e) => {
 *       setDateRange(r);
 *       setCustomStartDate(s);
 *       setCustomEndDate(e);
 *     }}
 *   />
 */
export default function DateRangeFilter({
  value,
  customStart = null,
  customEnd = null,
  onChange,
  presets = DEFAULT_PRESETS,
  variant = 'dropdown',
  align = 'right',
  className = '',
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Close both popovers on outside click
  useEffect(() => {
    if (!menuOpen && !calendarOpen) return;
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setMenuOpen(false);
        setCalendarOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen, calendarOpen]);

  const handlePresetClick = (preset) => {
    setMenuOpen(false);
    if (preset === 'custom') {
      onChange?.('custom', customStart, customEnd);
      setCalendarOpen(true);
      return;
    }
    onChange?.(preset, null, null);
  };

  const handleCustomPick = (dates) => {
    const [start, end] = dates || [];
    onChange?.('custom', start || null, end || null);
    if (start && end) setCalendarOpen(false);
  };

  const label = describeRange(value, customStart, customEnd);
  const alignClass = align === 'left' ? 'left-0' : 'right-0';

  // ----- TABS variant -----
  if (variant === 'tabs') {
    return (
      <div ref={wrapperRef} className={`relative inline-flex items-center gap-1 ${className}`}>
        {presets.map((p) => {
          const isActive = value === p;
          const isCustom = p === 'custom';
          return (
            <button
              key={p}
              type="button"
              onClick={() => handlePresetClick(p)}
              className={`
                px-3 py-1.5 text-sm rounded-lg transition-colors
                ${isActive
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'}
              `}
            >
              {isCustom && isActive ? label : (PRESET_LABELS[p] || p)}
            </button>
          );
        })}
        {calendarOpen && (
          <div className={`absolute top-full mt-2 ${alignClass} z-50 bg-white rounded-xl shadow-lg border border-gray-200 p-2`}>
            <DatePicker
              selected={customStart}
              onChange={handleCustomPick}
              startDate={customStart}
              endDate={customEnd}
              selectsRange
              inline
              maxDate={new Date()}
            />
          </div>
        )}
      </div>
    );
  }

  // ----- DROPDOWN variant (default, matches existing PerformanceMax style) -----
  return (
    <div ref={wrapperRef} className={`relative inline-block ${className}`}>
      <button
        type="button"
        onClick={() => { setMenuOpen((v) => !v); setCalendarOpen(false); }}
        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-700"
      >
        <CalendarIcon className="w-4 h-4 text-gray-500" />
        <span className="font-medium">{label}</span>
        <ChevronDown className="w-4 h-4 text-gray-400" />
      </button>

      {menuOpen && (
        <div
          className={`absolute ${alignClass} mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-3 pb-2 mb-1 border-b border-gray-100">
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Quick Select
            </h3>
          </div>
          {presets.map((p) => {
            const isActive = value === p;
            return (
              <button
                key={p}
                type="button"
                onClick={() => handlePresetClick(p)}
                className={`
                  w-full px-3 py-2 text-left text-sm transition-colors
                  ${isActive
                    ? 'text-blue-600 bg-blue-50/60 font-medium'
                    : 'text-gray-700 hover:bg-gray-50'}
                `}
              >
                {PRESET_LABELS[p] || p}
              </button>
            );
          })}
        </div>
      )}

      {calendarOpen && (
        <div className={`absolute ${alignClass} top-full mt-2 z-50 bg-white rounded-xl shadow-lg border border-gray-200 p-2`}>
          <DatePicker
            selected={customStart}
            onChange={handleCustomPick}
            startDate={customStart}
            endDate={customEnd}
            selectsRange
            inline
            maxDate={new Date()}
          />
        </div>
      )}
    </div>
  );
}
